import { Search, ShieldAlert } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router'
import { ContentBlocksFeed } from '@/components/abuse/content-blocks-feed'
import { DataTable } from '@/components/ui/data-table'
import { LoadingPage } from '@/components/ui/loading'
import { MetricCard } from '@/components/ui/metric-card'
import { StatusBadge } from '@/components/ui/status-badge'
import type { AtRiskAccount, EnforcementEntry } from '@/hooks/use-abuse'
import {
  useAbuseOverview,
  useAtRiskAccounts,
  useContentBlocks,
  useEnforcementLog,
} from '@/hooks/use-abuse'
import { formatNumber, formatPercent } from '@/lib/format'

const BOUNCE_THRESHOLD = 3
const COMPLAINT_THRESHOLD = 0.05
const ENFORCEMENT_PER_PAGE = 25

const ACTION_OPTIONS = ['all', 'pause', 'throttle', 'resume'] as const

export function AbusePage(): React.JSX.Element {
  const navigate = useNavigate()
  const { data: overview, isLoading: overviewLoading } = useAbuseOverview()
  const { data: atRisk, isLoading: atRiskLoading } = useAtRiskAccounts()
  const { data: blocks, isLoading: blocksLoading } = useContentBlocks()

  const [actionFilter, setActionFilter] = useState('all')
  const [accountSearch, setAccountSearch] = useState('')
  const [logPage, setLogPage] = useState(1)

  const { data: logData, isLoading: logLoading } = useEnforcementLog({
    page: logPage,
    per_page: ENFORCEMENT_PER_PAGE,
    action: actionFilter === 'all' ? undefined : actionFilter,
    account_id: accountSearch || undefined,
  })

  if (overviewLoading && !overview) {
    return <LoadingPage message="Loading abuse monitoring..." />
  }

  const atRiskColumns = [
    {
      key: 'account',
      label: 'Account',
      render: (row: AtRiskAccount) => <span className="font-medium">{row.user_email}</span>,
    },
    {
      key: 'tier',
      label: 'Tier',
      render: (row: AtRiskAccount) => <StatusBadge status={row.tier} />,
    },
    {
      key: 'status',
      label: 'Status',
      render: (row: AtRiskAccount) =>
        row.sending_paused ? (
          <StatusBadge status="paused" />
        ) : row.throttle_percent != null && row.throttle_percent < 100 ? (
          <StatusBadge status="warning">Throttled</StatusBadge>
        ) : (
          <StatusBadge status="active">Active</StatusBadge>
        ),
    },
    {
      key: 'bounce_rate',
      label: 'Bounce Rate',
      render: (row: AtRiskAccount) => (
        <span className={row.bounce_rate_24h > BOUNCE_THRESHOLD ? 'font-medium text-danger' : ''}>
          {formatPercent(row.bounce_rate_24h)}
        </span>
      ),
    },
    {
      key: 'complaint_rate',
      label: 'Complaint Rate',
      render: (row: AtRiskAccount) => (
        <span
          className={row.complaint_rate_24h > COMPLAINT_THRESHOLD ? 'font-medium text-danger' : ''}
        >
          {formatPercent(row.complaint_rate_24h)}
        </span>
      ),
    },
    {
      key: 'sent_24h',
      label: 'Sent (24h)',
      render: (row: AtRiskAccount) => formatNumber(row.sent_24h),
    },
  ]

  const enforcementColumns = [
    {
      key: 'time',
      label: 'Time',
      render: (row: EnforcementEntry) => (
        <span className="text-text-secondary">{new Date(row.created_at).toLocaleString()}</span>
      ),
    },
    {
      key: 'account',
      label: 'Account',
      render: (row: EnforcementEntry) => (
        <span className="font-mono text-sm">{row.account_id}</span>
      ),
    },
    {
      key: 'action',
      label: 'Action',
      render: (row: EnforcementEntry) => <StatusBadge status={row.action} />,
    },
    {
      key: 'reason',
      label: 'Reason',
      render: (row: EnforcementEntry) => <span className="text-text-secondary">{row.reason}</span>,
    },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <ShieldAlert className="h-6 w-6 text-admin-500" />
        <h1 className="text-2xl font-semibold text-text-primary">Abuse Monitoring</h1>
      </div>

      {/* Metric cards: 2 rows of 4 */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <MetricCard
          label="Accounts Paused"
          value={overview?.accounts_paused.toString() ?? '--'}
          variant={overview && overview.accounts_paused > 0 ? 'danger' : 'default'}
        />
        <MetricCard
          label="Accounts Throttled"
          value={overview?.accounts_throttled.toString() ?? '--'}
          variant={overview && overview.accounts_throttled > 0 ? 'warning' : 'default'}
        />
        <MetricCard
          label="Global Suppressions"
          value={overview ? formatNumber(overview.global_suppressions) : '--'}
        />
        <MetricCard
          label="Enforcement Actions (24h)"
          value={overview?.enforcement_actions_24h.toString() ?? '--'}
          variant={overview && overview.enforcement_actions_24h > 0 ? 'warning' : 'default'}
        />
        <MetricCard
          label="Content Blocks (24h)"
          value={overview?.content_blocks_24h.toString() ?? '--'}
          variant={overview && overview.content_blocks_24h > 0 ? 'danger' : 'default'}
        />
        <MetricCard
          label="Platform Bounce Rate"
          value={overview ? formatPercent(overview.bounce_rate_platform) : '--'}
          variant={overview && overview.bounce_rate_platform > 3 ? 'danger' : 'default'}
        />
        <MetricCard
          label="Platform Complaint Rate"
          value={overview ? formatPercent(overview.complaint_rate_platform) : '--'}
          variant={overview && overview.complaint_rate_platform > 0.05 ? 'danger' : 'default'}
        />
        <MetricCard
          label="DNSBL Listings"
          value={overview?.dnsbl_listings_active.toString() ?? '--'}
          variant={overview && overview.dnsbl_listings_active > 0 ? 'danger' : 'default'}
        />
      </div>

      {/* Middle section: two columns */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* At-Risk Accounts */}
        <div className="rounded-xl border border-surface-3 bg-surface-0 p-6">
          <h2 className="mb-4 text-sm font-medium text-text-primary">At-Risk Accounts</h2>
          <DataTable
            columns={atRiskColumns}
            data={atRisk ?? []}
            isLoading={atRiskLoading}
            onRowClick={(row: AtRiskAccount) => navigate(`/accounts/${row.id}`)}
            emptyMessage="No at-risk accounts."
          />
        </div>

        {/* Recent Content Blocks */}
        <div className="rounded-xl border border-surface-3 bg-surface-0 p-6">
          <h2 className="mb-4 text-sm font-medium text-text-primary">Recent Content Blocks</h2>
          <ContentBlocksFeed blocks={blocks ?? []} isLoading={blocksLoading} />
        </div>
      </div>

      {/* Enforcement Log */}
      <div>
        <h2 className="mb-3 text-sm font-medium text-text-primary">Enforcement Log</h2>
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <select
            value={actionFilter}
            onChange={(e) => {
              setActionFilter(e.target.value)
              setLogPage(1)
            }}
            className="rounded-lg border border-surface-3 bg-surface-0 px-3 py-2 text-sm text-text-primary focus:border-admin-500 focus:outline-none"
          >
            {ACTION_OPTIONS.map((a) => (
              <option key={a} value={a}>
                {a === 'all' ? 'All Actions' : a.charAt(0).toUpperCase() + a.slice(1)}
              </option>
            ))}
          </select>
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-tertiary" />
            <input
              type="text"
              placeholder="Search by account ID..."
              value={accountSearch}
              onChange={(e) => {
                setAccountSearch(e.target.value)
                setLogPage(1)
              }}
              className="w-full rounded-lg border border-surface-3 bg-surface-0 py-2 pl-9 pr-3 text-sm text-text-primary placeholder:text-text-tertiary focus:border-admin-500 focus:outline-none focus:ring-1 focus:ring-admin-500"
            />
          </div>
        </div>
        <DataTable
          columns={enforcementColumns}
          data={logData?.entries ?? []}
          isLoading={logLoading}
          pagination={
            logData
              ? { page: logData.page, perPage: logData.per_page, total: logData.total }
              : undefined
          }
          onPageChange={setLogPage}
          emptyMessage="No enforcement actions found."
        />
      </div>
    </div>
  )
}
